import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components/macro'

import { CloseIcon } from '../../components/Icons'
import { EmptyState, ModalBody, ModalHeader, ModalWrapper, SectionTitle, tokens } from '../../components/ui'
import BASE_API from '../../constants/baseApi'
import AUTH_TOKEN from '../../constants/authToken'

/*
 * Médicos que atendem na clínica.
 *
 * **Esta tela existe porque, até agora, ninguém conseguia criar esse vínculo.**
 * O `DoctorEstablishment` é lido em quatro lugares — a busca pública de
 * clínica, o detalhe da clínica, o perfil do médico e a home da instituição —
 * e não havia uma única tela que o escrevesse. Ele só era populado pelo script
 * de seed do ambiente local.
 *
 * O vínculo ficou aqui, no admin, e não no portal da instituição, porque é
 * credencial: quem confere o registro no Conselho é quem cadastra. Mesma regra
 * da inscrição secundária do CRM. O portal da clínica lista, e só.
 *
 * O par (médico, clínica) é único desde a migration 0067 — vincular duas vezes
 * devolve 400 com mensagem, em vez de duplicar a linha na lista da clínica.
 */

const DOCTORS_URL = `${BASE_API}/establishmentDoctors`
const SEARCH_URL = `${BASE_API}/allDoctors`

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${localStorage.getItem(AUTH_TOKEN)}`
})

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid #eef2f7;
  &:last-child { border-bottom: 0; }
`

const Nome = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${tokens.text};
`

const Detalhe = styled.div`
  font-size: 12px;
  color: ${tokens.textSoft};
  margin-top: 2px;
`

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #e8edf4;
  border-radius: 10px;
  font-size: 14px;
  outline: none;
  &:focus { border-color: ${tokens.brand}; }
`

const Button = styled.button`
  padding: 9px 18px;
  border-radius: 999px;
  border: 0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: ${p => (p.$ghost ? 'transparent' : tokens.brand)};
  color: ${p => (p.$ghost ? tokens.textSoft : '#fff')};
  &:disabled { opacity: 0.5; cursor: default; }
`

const Message = styled.div`
  margin-top: 12px;
  font-size: 13px;
  color: ${p => (p.$error ? '#b3261e' : tokens.textSoft)};
`

const formatarCRM = medico => {
  const inscricoes = medico.crms || []
  if (inscricoes.length === 0) return 'sem CRM cadastrado'
  return inscricoes
    .map(c => `${c.type || 'CRM'} ${c.number}${c.uf ? ` / ${c.uf}` : ''}`)
    .join(' · ')
}

const Doctors = memo(({ establishmentId, establishmentName, onRequestClose }) => {
  const [vinculados, setVinculados] = useState([])
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState(null)

  const [busca, setBusca] = useState('')
  const [resultados, setResultados] = useState([])
  const [buscando, setBuscando] = useState(false)
  const [salvando, setSalvando] = useState(null)

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      const resposta = await fetch(`${DOCTORS_URL}?id=${establishmentId}`, { headers: authHeaders() })
      if (!resposta.ok) throw new Error('Não foi possível carregar os médicos.')
      setVinculados(await resposta.json())
    } catch (e) {
      setErro(e.message)
    } finally {
      setCarregando(false)
    }
  }, [establishmentId])

  useEffect(() => { carregar() }, [carregar])

  /* Busca com debounce no endpoint de médicos do admin, que já casa nome,
     e-mail e as duas inscrições no Conselho. */
  useEffect(() => {
    const termo = busca.trim()
    if (termo.length < 2) {
      setResultados([])
      return undefined
    }
    const t = setTimeout(async () => {
      setBuscando(true)
      try {
        const url = `${SEARCH_URL}?page=1&pageSize=10&name=${encodeURIComponent(termo)}`
        const resposta = await fetch(url, { headers: authHeaders() })
        const dados = await resposta.json()
        setResultados(dados.results || dados || [])
      } catch (e) {
        setResultados([])
      } finally {
        setBuscando(false)
      }
    }, 400)
    return () => clearTimeout(t)
  }, [busca])

  const jaVinculados = useMemo(
    () => new Set(vinculados.map(m => m.id)),
    [vinculados]
  )

  const vincular = async medico => {
    setSalvando(medico.id)
    setErro(null)
    try {
      const resposta = await fetch(`${DOCTORS_URL}?id=${establishmentId}`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ doctor: medico.id })
      })
      const retorno = await resposta.json().catch(() => null)
      if (!resposta.ok) throw new Error((retorno && retorno.detail) || 'Não foi possível vincular.')
      setBusca('')
      setResultados([])
      await carregar()
    } catch (e) {
      setErro(e.message)
    } finally {
      setSalvando(null)
    }
  }

  const desvincular = async medico => {
    /* Confirmação porque o efeito não é local: o médico some da busca pública
       desta clínica e da lista que a própria clínica vê no portal dela. */
    const nome = medico.name || 'este médico'
    if (!window.confirm(`Remover ${nome} de ${establishmentName || 'esta clínica'}?`)) return

    setSalvando(medico.id)
    setErro(null)
    try {
      const url = `${DOCTORS_URL}?id=${establishmentId}&doctor=${medico.id}`
      const resposta = await fetch(url, { method: 'DELETE', headers: authHeaders() })
      if (!resposta.ok) throw new Error('Não foi possível remover o vínculo.')
      await carregar()
    } catch (e) {
      setErro(e.message)
    } finally {
      setSalvando(null)
    }
  }

  return (
    <ModalWrapper>
      <ModalHeader>
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: tokens.text }}>Médicos da clínica</div>
          <div style={{ fontSize: 13, color: tokens.textSoft, marginTop: 2 }}>
            {establishmentName}
          </div>
        </div>
        <button onClick={onRequestClose} style={{ border: 0, background: 'none', cursor: 'pointer' }}>
          <CloseIcon />
        </button>
      </ModalHeader>

      <ModalBody>
        <SectionTitle>Vincular médico</SectionTitle>
        <Input
          value={busca}
          onChange={e => setBusca(e.target.value)}
          placeholder='Buscar por nome, e-mail ou CRM'
        />
        {busca.trim().length === 1 && (
          <Message>Digite ao menos dois caracteres.</Message>
        )}
        {buscando && <Message>Buscando...</Message>}

        {resultados.map(medico => {
          const vinculado = jaVinculados.has(medico.id)
          return (
            <Row key={medico.id}>
              <div>
                <Nome>{medico.name || '—'}</Nome>
                <Detalhe>{formatarCRM(medico)} · {medico.login || medico.user_login || '—'}</Detalhe>
              </div>
              <Button
                onClick={() => vincular(medico)}
                disabled={vinculado || salvando === medico.id}
              >
                {vinculado ? 'Já vinculado' : salvando === medico.id ? 'Vinculando...' : 'Vincular'}
              </Button>
            </Row>
          )
        })}

        <div style={{ marginTop: 26 }}>
          <SectionTitle>Atendem aqui ({vinculados.length})</SectionTitle>
        </div>

        {carregando && <Message>Carregando...</Message>}

        {!carregando && vinculados.length === 0 && (
          <EmptyState>
            Nenhum médico vinculado. A clínica não aparece na busca de médicos
            enquanto não houver ao menos um.
          </EmptyState>
        )}

        {!carregando && vinculados.map(medico => (
          <Row key={medico.id}>
            <div>
              <Nome>{medico.name || '—'}</Nome>
              <Detalhe>{formatarCRM(medico)} · {medico.login || '—'}</Detalhe>
            </div>
            <Button $ghost onClick={() => desvincular(medico)} disabled={salvando === medico.id}>
              {salvando === medico.id ? 'Removendo...' : 'Remover'}
            </Button>
          </Row>
        ))}

        {erro && <Message $error>{erro}</Message>}
      </ModalBody>
    </ModalWrapper>
  )
})

export default Doctors
